import React, { use } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation } from '@apollo/client'

import { FormattedMessage } from '@divvy-web/i18n'
import { FormContext } from '@divvy-web/skylab.form'
import { TOAST_TYPE_DANGER, useToast } from '@divvy-web/skylab.toast'

import { dateStringToEpoch } from '@sputnik/components/utils'
import UpdateBeneficialOwners from '@sputnik/pages/gql/mutations/UpdateBeneficialOwners.gql'
import { getEmptyCompanyOwner } from '@sputnik/utils/dataUtils'
import { logError } from '@sputnik/utils/loggerUtils'
import { epochToDate, prepCompanyOwners } from '@sputnik/utils/mutationUtils'

const getEmptyAddress = () => ({
  city: '',
  countryCode: '',
  postalCode: '',
  state: '',
  street: '',
  streetAdditional: '',
})

const generateCompanyOwnersList = (formValues) => {
  return Array.from({ length: 4 }, getEmptyCompanyOwner)?.reduce((acc, owner, idx) => {
    if (!formValues[`owner${idx}_isCreated`]) {
      return acc
    }

    if (formValues[`owner${idx}_isAuthorizedSigner`]) {
      acc.unshift({ id: formValues?.authorizedSignerId })
      return acc
    }

    for (const key of Reflect.ownKeys(owner)) {
      if (key.startsWith('address')) {
        if (!owner?.address) {
          owner.address = getEmptyAddress()
        }

        const addressPart = key?.slice(7)
        const addressKey = addressPart[0]?.toLowerCase() + addressPart?.slice(1)
        owner.address[addressKey] = formValues[`owner${idx}_${key}`]
        Reflect.deleteProperty(owner, key)
      } else if (key?.match(/dob$/i)) {
        owner[key] = epochToDate(formValues[`owner${idx}_${key}`])
      } else if (key?.match(/expiration$/i)) {
        const expirationEpoch = dateStringToEpoch(formValues[`owner${idx}_${key}`])
        owner[key] = epochToDate(expirationEpoch)
      } else {
        owner[key] = formValues[`owner${idx}_${key}`]
      }
    }

    if (owner._passportToggle) {
      if (owner.passportNumber === '') {
        Reflect.deleteProperty(owner, 'passportNumber')
      }
      owner.ssn = ''
    } else {
      if (owner.ssn === '') {
        Reflect.deleteProperty(owner, 'ssn')
        owner.passportExpiration = null
      } else {
        owner.passportExpiration = ''
      }
      owner.passportCountry = ''
      owner.passportNumber = ''
    }

    Reflect.deleteProperty(owner, 'ssnDisplay')
    Reflect.deleteProperty(owner, 'passportNumberDisplay')
    Reflect.deleteProperty(owner, 'dobDisplay')
    Reflect.deleteProperty(owner, 'isAuthorizedSigner')

    acc.push(owner)

    return acc
  }, [])
}

export const useUpdateCompanyOwners = () => {
  const showToast = useToast()
  const { appId: creditApplicationId } = useParams()
  const { formValues } = use(FormContext)
  const beneficialOwners = generateCompanyOwnersList(formValues)
  const formattedBeneficialOwners = prepCompanyOwners(beneficialOwners)

  const [updateCompanyOwners, { loading }] = useMutation(UpdateBeneficialOwners, {
    onError: (e) => {
      logError(e, {
        attributes: {
          action: 'Failure',
          creditAppId: creditApplicationId,
          result: `Error occured when updating company owners`,
        },
        eventName: 'UpdateCompanyOwners',
      })

      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Error occured while saving changes.'
          id='sputnik.useUpdateCompanyOwners__v0GWGq'
        />,
        {
          autoHideDelay: 5000,
          dataTestId: 'company-owners-update-fail',
        },
      )
    },
    variables: {
      beneficialOwners: formattedBeneficialOwners,
      creditApplicationId,
      hasBeneficialOwner: !!formattedBeneficialOwners.length,
    },
  })
  return { loading, updateCompanyOwners }
}
